/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const UPDATE_REPUTATION_WITH_AUTH_DISCRIMINATOR = new Uint8Array([
  61, 236, 225, 167, 134, 43, 53, 93,
]);

export function getUpdateReputationWithAuthDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_REPUTATION_WITH_AUTH_DISCRIMINATOR,
  );
}

export type UpdateReputationWithAuthInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountReputationMetrics extends string | AccountMeta<string> = string,
  TAccountAuthorization extends string | AccountMeta<string> = string,
  TAccountUsageRecord extends string | AccountMeta<string> = string,
  TAccountAuthorizedSource extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountReputationMetrics extends string
        ? WritableAccount<TAccountReputationMetrics>
        : TAccountReputationMetrics,
      TAccountAuthorization extends string
        ? WritableAccount<TAccountAuthorization>
        : TAccountAuthorization,
      TAccountUsageRecord extends string
        ? WritableAccount<TAccountUsageRecord>
        : TAccountUsageRecord,
      TAccountAuthorizedSource extends string
        ? WritableSignerAccount<TAccountAuthorizedSource> &
            AccountSignerMeta<TAccountAuthorizedSource>
        : TAccountAuthorizedSource,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateReputationWithAuthInstructionData = {
  discriminator: ReadonlyUint8Array;
  reputationChange: bigint;
  transactionSignature: string;
  metadata: Option<string>;
  nonce: Option<string>;
};

export type UpdateReputationWithAuthInstructionDataArgs = {
  reputationChange: number | bigint;
  transactionSignature: string;
  metadata: OptionOrNullable<string>;
  nonce: OptionOrNullable<string>;
};

export function getUpdateReputationWithAuthInstructionDataEncoder(): Encoder<UpdateReputationWithAuthInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["reputationChange", getI64Encoder()],
      [
        "transactionSignature",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      [
        "metadata",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      [
        "nonce",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: UPDATE_REPUTATION_WITH_AUTH_DISCRIMINATOR,
    }),
  );
}

export function getUpdateReputationWithAuthInstructionDataDecoder(): Decoder<UpdateReputationWithAuthInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["reputationChange", getI64Decoder()],
    [
      "transactionSignature",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    [
      "metadata",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    [
      "nonce",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getUpdateReputationWithAuthInstructionDataCodec(): Codec<
  UpdateReputationWithAuthInstructionDataArgs,
  UpdateReputationWithAuthInstructionData
> {
  return combineCodec(
    getUpdateReputationWithAuthInstructionDataEncoder(),
    getUpdateReputationWithAuthInstructionDataDecoder(),
  );
}

export type UpdateReputationWithAuthAsyncInput<
  TAccountAgent extends string = string,
  TAccountReputationMetrics extends string = string,
  TAccountAuthorization extends string = string,
  TAccountUsageRecord extends string = string,
  TAccountAuthorizedSource extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent whose reputation is being updated */
  agent: Address<TAccountAgent>;
  /** Agent reputation metrics */
  reputationMetrics?: Address<TAccountReputationMetrics>;
  /** Authorization account (PDA) */
  authorization?: Address<TAccountAuthorization>;
  /** Usage record account (optional, for audit trail) */
  usageRecord: Address<TAccountUsageRecord>;
  /** Authorized source (e.g., PayAI facilitator) */
  authorizedSource: TransactionSigner<TAccountAuthorizedSource>;
  /** System program */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  reputationChange: UpdateReputationWithAuthInstructionDataArgs["reputationChange"];
  transactionSignature: UpdateReputationWithAuthInstructionDataArgs["transactionSignature"];
  metadata: UpdateReputationWithAuthInstructionDataArgs["metadata"];
  nonce: UpdateReputationWithAuthInstructionDataArgs["nonce"];
};

export async function getUpdateReputationWithAuthInstructionAsync<
  TAccountAgent extends string,
  TAccountReputationMetrics extends string,
  TAccountAuthorization extends string,
  TAccountUsageRecord extends string,
  TAccountAuthorizedSource extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateReputationWithAuthAsyncInput<
    TAccountAgent,
    TAccountReputationMetrics,
    TAccountAuthorization,
    TAccountUsageRecord,
    TAccountAuthorizedSource,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UpdateReputationWithAuthInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountReputationMetrics,
    TAccountAuthorization,
    TAccountUsageRecord,
    TAccountAuthorizedSource,
    TAccountSystemProgram,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agent: { value: input.agent ?? null, isWritable: false },
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    authorization: { value: input.authorization ?? null, isWritable: true },
    usageRecord: { value: input.usageRecord ?? null, isWritable: true },
    authorizedSource: {
      value: input.authorizedSource ?? null,
      isWritable: true,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.reputationMetrics.value) {
    accounts.reputationMetrics.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 112, 117, 116, 97, 116, 105, 111, 110, 95, 109, 101, 116,
            114, 105, 99, 115,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
      ],
    });
  }
  if (!accounts.authorization.value) {
    accounts.authorization.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([97, 103, 101, 110, 116, 95, 97, 117, 116, 104]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
        getAddressEncoder().encode(
          expectAddress(accounts.authorizedSource.value),
        ),
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ).encode(expectSome(args.nonce)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.authorization),
      getAccountMeta(accounts.usageRecord),
      getAccountMeta(accounts.authorizedSource),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateReputationWithAuthInstructionDataEncoder().encode(
      args as UpdateReputationWithAuthInstructionDataArgs,
    ),
    programAddress,
  } as UpdateReputationWithAuthInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountReputationMetrics,
    TAccountAuthorization,
    TAccountUsageRecord,
    TAccountAuthorizedSource,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type UpdateReputationWithAuthInput<
  TAccountAgent extends string = string,
  TAccountReputationMetrics extends string = string,
  TAccountAuthorization extends string = string,
  TAccountUsageRecord extends string = string,
  TAccountAuthorizedSource extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent whose reputation is being updated */
  agent: Address<TAccountAgent>;
  /** Agent reputation metrics */
  reputationMetrics: Address<TAccountReputationMetrics>;
  /** Authorization account (PDA) */
  authorization: Address<TAccountAuthorization>;
  /** Usage record account (optional, for audit trail) */
  usageRecord: Address<TAccountUsageRecord>;
  /** Authorized source (e.g., PayAI facilitator) */
  authorizedSource: TransactionSigner<TAccountAuthorizedSource>;
  /** System program */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  reputationChange: UpdateReputationWithAuthInstructionDataArgs["reputationChange"];
  transactionSignature: UpdateReputationWithAuthInstructionDataArgs["transactionSignature"];
  metadata: UpdateReputationWithAuthInstructionDataArgs["metadata"];
  nonce: UpdateReputationWithAuthInstructionDataArgs["nonce"];
};

export function getUpdateReputationWithAuthInstruction<
  TAccountAgent extends string,
  TAccountReputationMetrics extends string,
  TAccountAuthorization extends string,
  TAccountUsageRecord extends string,
  TAccountAuthorizedSource extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateReputationWithAuthInput<
    TAccountAgent,
    TAccountReputationMetrics,
    TAccountAuthorization,
    TAccountUsageRecord,
    TAccountAuthorizedSource,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): UpdateReputationWithAuthInstruction<
  TProgramAddress,
  TAccountAgent,
  TAccountReputationMetrics,
  TAccountAuthorization,
  TAccountUsageRecord,
  TAccountAuthorizedSource,
  TAccountSystemProgram,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agent: { value: input.agent ?? null, isWritable: false },
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    authorization: { value: input.authorization ?? null, isWritable: true },
    usageRecord: { value: input.usageRecord ?? null, isWritable: true },
    authorizedSource: {
      value: input.authorizedSource ?? null,
      isWritable: true,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.authorization),
      getAccountMeta(accounts.usageRecord),
      getAccountMeta(accounts.authorizedSource),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateReputationWithAuthInstructionDataEncoder().encode(
      args as UpdateReputationWithAuthInstructionDataArgs,
    ),
    programAddress,
  } as UpdateReputationWithAuthInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountReputationMetrics,
    TAccountAuthorization,
    TAccountUsageRecord,
    TAccountAuthorizedSource,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type ParsedUpdateReputationWithAuthInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Agent whose reputation is being updated */
    agent: TAccountMetas[0];
    /** Agent reputation metrics */
    reputationMetrics: TAccountMetas[1];
    /** Authorization account (PDA) */
    authorization: TAccountMetas[2];
    /** Usage record account (optional, for audit trail) */
    usageRecord: TAccountMetas[3];
    /** Authorized source (e.g., PayAI facilitator) */
    authorizedSource: TAccountMetas[4];
    /** System program */
    systemProgram: TAccountMetas[5];
    /** Clock for timestamps */
    clock: TAccountMetas[6];
  };
  data: UpdateReputationWithAuthInstructionData;
};

export function parseUpdateReputationWithAuthInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateReputationWithAuthInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 7) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agent: getNextAccount(),
      reputationMetrics: getNextAccount(),
      authorization: getNextAccount(),
      usageRecord: getNextAccount(),
      authorizedSource: getNextAccount(),
      systemProgram: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getUpdateReputationWithAuthInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
